import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { vehicleSlice, selectVehicleError } from "./../../store";
import { AuthService, VehicleService, EmployeeService, EventsService, computeDaysFromTodayToDate } from "../../services";
import { buildExternalDriverOptions, formatDriverDropdownLabel, isDriverEligibleEmployee, mergeDriverOptionsById } from "../../shared/utils/driverDropdownOptions";
import { Archive, Upload, ArrowClockwise } from "react-bootstrap-icons";
import { Spinner, Breadcrumb, BreadcrumbItem, Tabs, Tab, Modal, Button } from "react-bootstrap";
import DatePicker from "react-datepicker";
import Select from 'react-select';
import moment from 'moment';
import {
  SEATING_CAPACITY_OPTIONS,
  FUEL_TYPE, FUEL_TYPE_TEXT,
  VEHICLE_TITLE, VEHICLE_TITLE_TEXT,
  LIFT_EQUIPPED, LIFT_EQUIPPED_TEXT,
  useUnsavedChangesGuard,
  useFormDirtyTracker,
} from "../../shared";

const SEATING_CAPACITY_VALUE_SET = new Set(SEATING_CAPACITY_OPTIONS.map((o) => String(o.value)));
const LIFT_VALUE_SET = new Set(Object.values(LIFT_EQUIPPED));
const FUEL_VALUE_SET = new Set(Object.values(FUEL_TYPE));
const TITLE_VALUE_SET = new Set(Object.values(VEHICLE_TITLE));

const UpdateVehicle = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const canArchiveVehicle = AuthService.canArchiveVehicle();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const validTabs = ['basicInfo', 'complianceDeadlines'];
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(validTabs.includes(tabFromUrl) ? tabFromUrl : 'basicInfo');
  const vehicles = useSelector((state) => state.vehicles && state.vehicles.vehicles);
  const currentVehicle = vehicles.find((item) => String(item.id) === String(params.id)) || undefined;
  const { updateVehicle, fetchAllVehicles } = vehicleSlice.actions;
  const error = useSelector(selectVehicleError);

  // Basic Information
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [responsibleDriver, setResponsibleDriver] = useState(null);
  const [capacity, setCapacity] = useState('');
  const [mileage, setMileage] = useState('');
  const [make, setMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [year, setYear] = useState('');
  const [vin, setVin] = useState('');
  const [tag, setTag] = useState('');
  const [gps, setGps] = useState('');
  const [ezpass, setEzpass] = useState('');
  const [equipment, setEquipment] = useState('');
  const [hasLiftEquip, setHasLiftEquip] = useState('');
  const [fuelType, setFuelType] = useState('');
  const [title, setTitle] = useState('');
  const [titleOther, setTitleOther] = useState('');
  const [titleNumber, setTitleNumber] = useState('');

  // Compliance & Deadlines
  const [insuranceStartDate, setInsuranceStartDate] = useState(null);
  const [vehicleRegistrationDate, setVehicleRegistrationDate] = useState(null);
  const [emissionsDate, setEmissionsDate] = useState(null);

  // Check List
  const [checklist, setChecklist] = useState(['']);

  // Additional Information
  const [note, setNote] = useState('');

  // Drivers list
  const [drivers, setDrivers] = useState([]);

  // Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCopyChecklistModal, setShowCopyChecklistModal] = useState(false);
  const [copyChecklistSourceVehicleId, setCopyChecklistSourceVehicleId] = useState(null);
  /** false until GET /vehicles/:id finishes — prevents a late response from overwriting fields (e.g. title_number) the user already typed. */
  const [vehicleDataReady, setVehicleDataReady] = useState(false);

  const { isDirty, resetBaseline } = useFormDirtyTracker({
    vehicleNumber,
    responsibleDriver: responsibleDriver?.value ?? '',
    capacity, mileage, make, vehicleModel, year, vin, tag, gps, ezpass, equipment,
    hasLiftEquip, fuelType, title, titleOther, titleNumber,
    insuranceStartDate, vehicleRegistrationDate, emissionsDate,
    checklist, note,
  });
  const { tryNavigate } = useUnsavedChangesGuard(isDirty);
  /** On save, read the actual input value. Browser autofill can show text without firing React onChange, so state would stay "" while the DOM has the real value. */
  const titleNumberInputRef = useRef(null);

  const otherVehiclesForChecklistCopy = useMemo(() => {
    return (vehicles || [])
      .filter((v) => String(v.id) !== String(params.id))
      .slice()
      .sort((a, b) =>
        String(a.vehicle_number ?? "").localeCompare(String(b.vehicle_number ?? ""), undefined, {
          numeric: true,
        })
      );
  }, [vehicles, params.id]);

  useEffect(() => {
    if (!AuthService.canAddOrEditVechiles()) {
      window.alert('You haven\'t login yet OR this user does not have access to this page. Please change an Dispatcher or admin account to login.')
      AuthService.logout();
      navigate(`/login`);
    }
    if (!currentVehicle) {
      dispatch(fetchAllVehicles());
    }
    Promise.all([
      EmployeeService.getAllEmployees(),
      EmployeeService.getExternalUserPermissionsList(EventsService.site),
    ])
      .then(([employeeResponse, externalPermissionResponse]) => {
        const employees = employeeResponse?.data || [];
        const internalDrivers = employees.filter(isDriverEligibleEmployee);
        const externalDrivers = buildExternalDriverOptions(externalPermissionResponse?.data || []);
        setDrivers(mergeDriverOptionsById(internalDrivers, externalDrivers));
      })
      .catch(() => {
        setDrivers([]);
      });
  }, []);

  /** Use GET one vehicle (authoritative). Do not only hydrate from Redux `vehicles` — list payloads can be stale, which could overwrite e.g. title_number after a fresh fetch. */
  useEffect(() => {
    if (!params.id) return;
    setVehicleDataReady(false);
    let cancelled = false;
    VehicleService.getVehicle(params.id)
      .then((res) => {
        if (cancelled) return;
        const v = res?.data;
        if (!v) return;
        setVehicleNumber(String(v.vehicle_number ?? ''));
        setResponsibleDriver(
          v.responsible_driver
            ? {
                value:
                  v.responsible_driver_id !== undefined &&
                  v.responsible_driver_id !== null &&
                  v.responsible_driver_id !== ''
                    ? String(v.responsible_driver_id)
                    : '',
                label: v.responsible_driver || ''
              }
            : null
        );
        setCapacity(v.capacity?.toString() || '');
        setMileage(v.mileage?.toString() || '');
        setMake(String(v.make ?? ''));
        setVehicleModel(String(v.vehicle_model ?? ''));
        setYear(String(v.year ?? ''));
        setVin(String(v.vin ?? ''));
        setTag(String(v.tag ?? ''));
        setGps(String(v.gps_tag ?? ''));
        setEzpass(String(v.ezpass ?? ''));
        setEquipment(String(v.equipment ?? ''));
        setHasLiftEquip(
          v.has_lift_equip === true ? 'true' : v.has_lift_equip === false ? 'false' : ''
        );
        setFuelType(v.fuel_type || '');
        setTitle(v.title || '');
        setTitleOther(v.title_other || '');
        setTitleNumber(String(v.title_number ?? ''));
        setInsuranceStartDate(
          v.insurance_start_date
            ? VehicleService.convertToDate(v.insurance_start_date)
            : v.insurance_expire_on
              ? VehicleService.convertToDate(v.insurance_expire_on)
              : null
        );
        setVehicleRegistrationDate(
          v.vehicle_registration_date
            ? VehicleService.convertToDate(v.vehicle_registration_date)
            : v.title_registration_on
              ? VehicleService.convertToDate(v.title_registration_on)
              : null
        );
        setEmissionsDate(
          v.emissions_date
            ? VehicleService.convertToDate(v.emissions_date)
            : v.emission_test_on
              ? VehicleService.convertToDate(v.emission_test_on)
              : null
        );
        setChecklist(v.checklist || ['']);
        setNote(v.note || '');
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Failed to load vehicle for edit", err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setVehicleDataReady(true);
          window.setTimeout(() => resetBaseline(), 0);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [params.id]);

  const redirectTo = () => {
    const qs = new URLSearchParams(window.location.search);
    const redirect = qs.get('redirect');
    if (redirect === 'schedule') {
      tryNavigate(`/trans-routes/schedule`);
    } else {
      if (redirect === 'list') {
        tryNavigate(`/vehicles/list`);
      } else {
        tryNavigate(`/trans-routes/dashboard`);
      }
    }
  }

  const redirectToView = () => {
    resetBaseline();
    tryNavigate(`/vehicles/${params.id}`);
  }

  /** Cancel / Back: return to view vehicle (not list), keep tab when possible */
  const navigateBackToView = () => {
    const viewTabs = ['basicInfo', 'complianceDeadlines', 'documents'];
    const tabFromQuery = searchParams.get('tab');
    const tab =
      tabFromQuery && viewTabs.includes(tabFromQuery)
        ? tabFromQuery
        : validTabs.includes(activeTab)
          ? activeTab
          : 'basicInfo';
    if (tab === 'basicInfo') {
      tryNavigate(`/vehicles/${params.id}`);
    } else {
      tryNavigate(`/vehicles/${params.id}?tab=${tab}`);
    }
  };

  const addItemToArray = () => {
    const arr = [...checklist, ''];
    setChecklist(arr);
  };

  const openCopyChecklistModal = () => {
    setCopyChecklistSourceVehicleId(null);
    setShowCopyChecklistModal(true);
    const others = (vehicles || []).filter((v) => String(v.id) !== String(params.id));
    if (others.length === 0) {
      dispatch(fetchAllVehicles());
    }
  };

  const closeCopyChecklistModal = () => {
    setShowCopyChecklistModal(false);
    setCopyChecklistSourceVehicleId(null);
  };

  const applyChecklistFromSelectedVehicle = () => {
    const source = vehicles.find((v) => String(v.id) === String(copyChecklistSourceVehicleId));
    if (!source) {
      window.alert("Please select a vehicle.");
      return;
    }
    const srcList = source.checklist;
    const next =
      Array.isArray(srcList) && srcList.length > 0 ? [...srcList] : [""];
    setChecklist(next);
    closeCopyChecklistModal();
  };

  const formatDateForBackend = (date) => {
    if (!date) return '';
    return moment(date).format('MM/DD/YYYY');
  }

  const buildVehicleData = () => {
    const el = titleNumberInputRef.current;
    const fromDom =
      el && (typeof document !== "undefined") && document.body?.contains?.(el)
        ? String(el.value ?? "")
        : String(titleNumber ?? "");
    return {
      // Basic Information
      vehicle_number: vehicleNumber,
      responsible_driver: responsibleDriver?.label || '',
      responsible_driver_id: responsibleDriver?.value || '',
      capacity: parseInt(capacity) || 0,
      mileage: parseInt(mileage) || 0,
      make,
      vehicle_model: vehicleModel,
      year,
      vin,
      tag,
      gps_tag: gps,
      ezpass,
      equipment,
      has_lift_equip: hasLiftEquip === 'true',
      fuel_type: fuelType,
      title,
      title_other: titleOther,
      title_number: fromDom,
      // Duplicate for any proxy / old API that only reads camelCase
      titleNumber: fromDom,
      
      // Compliance & Deadlines
      insurance_start_date: formatDateForBackend(insuranceStartDate),
      vehicle_registration_date: formatDateForBackend(vehicleRegistrationDate),
      emissions_date: formatDateForBackend(emissionsDate),
      emission_test_on: formatDateForBackend(emissionsDate),
      
      // Legacy fields for backward compatibility
      insurance_expire_on: formatDateForBackend(insuranceStartDate),
      title_registration_on: formatDateForBackend(vehicleRegistrationDate),
      
      // Check List
      checklist,
      
      // Additional Information
      note,
      
      // System fields
      status: 'active'
    };
  };

  const validateVehicle = () => {
    const errors = [];
    
    // Required fields validation
    if (!vehicleNumber || vehicleNumber.trim() === '') {
      errors.push('Vehicle Number');
    }
    if (!tag || tag.trim() === '') {
      errors.push('License Plate');
    }
    
    if (errors.length > 0) {
      window.alert(`Please fill in the following required fields:\n${errors.join('\n')}`);
      return false;
    }
    return true;
  };

  const saveVehicle = () => {
    if (!vehicleDataReady) {
      return;
    }
    if (!validateVehicle()) {
      return;
    }

    const data = buildVehicleData();
    dispatch(updateVehicle({id: params.id, data, redirectFun: redirectToView}));
  }

  const triggerShowDeleteModal = () => {
    if (!canArchiveVehicle) return;
    setShowDeleteModal(true);
  }

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  }

  const isArchivedVehicle = `${currentVehicle?.status || ''}`.toLowerCase() === 'inactive';

  const updateVehicleArchiveStatus = (nextStatus) => {
    if (!canArchiveVehicle || !vehicleDataReady) return;
    const data = buildVehicleData();
    data.status = nextStatus;
    setShowDeleteModal(false);
    dispatch(
      updateVehicle({
        id: params.id,
        data,
        redirectFun: () => {
          resetBaseline();
          tryNavigate(nextStatus === 'inactive' ? '/vehicles/list?tab=archived' : '/vehicles/list');
        },
      })
    );
  }


  const driverSelectOptions = useMemo(() => {
    const base = [
      { value: '', label: '' },
      ...drivers.map((driver) => ({
        value:
          driver?.id !== undefined && driver?.id !== null && driver?.id !== ''
            ? String(driver.id)
            : '',
        label: formatDriverDropdownLabel(driver),
      })),
    ];
    const v = responsibleDriver?.value;
    if (v !== undefined && v !== null && `${v}`.trim() !== '') {
      const exists = base.some((o) => String(o.value) === String(v));
      if (!exists) {
        const label =
          `${responsibleDriver?.label || ''}`.trim() || String(v);
        return [...base, { value: String(v), label }];
      }
    }
    return base;
  }, [drivers, responsibleDriver]);

  // Custom styles for react-select
  const selectStyles = {
    control: (baseStyles) => ({
      ...baseStyles,
      minWidth: '200px',
      borderRadius: '8px'
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (baseStyles) => ({
      ...baseStyles,
      paddingRight: '8px',
      color: '#333',
      '&:hover': {
        color: '#000',
      },
    }),
    valueContainer: (baseStyles) => ({
      ...baseStyles,
      height: '43px',
      padding: '0 8px',
    }),
    input: (baseStyles) => ({
      ...baseStyles,
      margin: '0px',
      padding: '0px',
      height: '30px',
      width: '290px'
    }),
    singleValue: (baseStyles) => ({
      ...baseStyles,
      margin: '0px',
    }),
  };

  return (
    <>
      <div className="list row mb-4">
        <Breadcrumb>
          <Breadcrumb.Item href="/trans-routes/dashboard">Transportation</Breadcrumb.Item>
          <Breadcrumb.Item href="/vehicles/list">
            Vehicles Information
          </Breadcrumb.Item>
          <Breadcrumb.Item active>
            Update Vehicle Information
          </Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
          <h4>Update Vehicle Information <button className="btn btn-link btn-sm" onClick={() => {navigateBackToView()}}>Back</button></h4> 
        </div>
      </div>
      <div className="app-main-content-list-container form-page">
        <div className="app-main-content-list-func-container">
          {!vehicleDataReady && (
            <div className="d-flex align-items-center justify-content-center py-5 gap-2" role="status" aria-live="polite">
              <Spinner animation="border" size="sm" />
              <span>Loading vehicle…</span>
            </div>
          )}
          {vehicleDataReady && (
          <>
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} id="vehicles-tab">
            <Tab eventKey="basicInfo" title="Basic Information">
              <h6 className="text-primary">Basic Information</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Vehicle Number <span className="required">*</span></div>
                  <input type="text" placeholder="e.g., 101" value={vehicleNumber} onChange={e => setVehicleNumber(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">Responsible Driver</div>
                  <Select
                    value={responsibleDriver}
                    onChange={setResponsibleDriver}
                    options={driverSelectOptions}
                    styles={selectStyles}
                    placeholder="e.g., John Smith"
                    isClearable
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">Seating Capacity</div>
                  <select value={capacity} onChange={e => setCapacity(e.target.value)}>
                    <option value="">Select...</option>
                    {SEATING_CAPACITY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    {capacity !== '' && capacity != null && !SEATING_CAPACITY_VALUE_SET.has(String(capacity)) && (
                      <option value={String(capacity)}>{String(capacity)}</option>
                    )}
                  </select>
                </div>
                <div className="me-4">
                  <div className="field-label">Mileage</div>
                  <input type="number" placeholder="e.g., 48000" value={mileage} onChange={e => setMileage(e.target.value)}/>
                </div>
              </div>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Make</div>
                  <input type="text" placeholder="e.g., Ford" value={make} onChange={e => setMake(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">Model</div>
                  <input type="text" placeholder="e.g., T350" value={vehicleModel} onChange={e => setVehicleModel(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">Year</div>
                  <input type="text" placeholder="e.g., 2018" value={year} onChange={e => setYear(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">VIN Number</div>
                  <input type="text" placeholder="e.g., 1FBAX2CM9KKA34959" value={vin} onChange={e => setVin(e.target.value)}/>
                </div>
              </div>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">License Plate <span className="required">*</span></div>
                  <input type="text" placeholder="e.g., 91579HT" value={tag} onChange={e => setTag(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">GPS ID</div>
                  <input type="text" placeholder="e.g., 609671" value={gps} onChange={e => setGps(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">E-ZPass</div>
                  <input type="text" placeholder="e.g., NY12345" value={ezpass} onChange={e => setEzpass(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">Lift Equipped</div>
                  <select value={hasLiftEquip} onChange={e => setHasLiftEquip(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(LIFT_EQUIPPED).map(key => (
                      <option key={key} value={LIFT_EQUIPPED[key]}>{LIFT_EQUIPPED_TEXT[LIFT_EQUIPPED[key]]}</option>
                    ))}
                    {hasLiftEquip !== '' && hasLiftEquip != null && !LIFT_VALUE_SET.has(String(hasLiftEquip)) && (
                      <option value={String(hasLiftEquip)}>{String(hasLiftEquip)}</option>
                    )}
                  </select>
                </div>
                <div className="me-4">
                  <div className="field-label">Equipment</div>
                  <input
                    type="text"
                    placeholder="e.g., Dash Camera"
                    value={equipment}
                    onChange={(e) => setEquipment(e.target.value)}
                  />
                </div>
              </div>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Fuel Type</div>
                  <select value={fuelType} onChange={e => setFuelType(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(FUEL_TYPE).map(key => (
                      <option key={key} value={FUEL_TYPE[key]}>{FUEL_TYPE_TEXT[FUEL_TYPE[key]]}</option>
                    ))}
                    {fuelType !== '' && fuelType != null && !FUEL_VALUE_SET.has(String(fuelType)) && (
                      <option value={String(fuelType)}>{FUEL_TYPE_TEXT[fuelType] || String(fuelType)}</option>
                    )}
                  </select>
                </div>
                <div className="me-4">
                  <div className="field-label">Title</div>
                  <select value={title} onChange={e => setTitle(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(VEHICLE_TITLE).map(key => (
                      <option key={key} value={VEHICLE_TITLE[key]}>{VEHICLE_TITLE_TEXT[VEHICLE_TITLE[key]]}</option>
                    ))}
                    {title !== '' && title != null && !TITLE_VALUE_SET.has(String(title)) && (
                      <option value={String(title)}>{VEHICLE_TITLE_TEXT[title] || String(title)}</option>
                    )}
                  </select>
                </div>
                {title === 'other' && (
                  <div className="me-4">
                    <div className="field-label">Title (Other)</div>
                    <input type="text" placeholder="Please specify..." value={titleOther} onChange={e => setTitleOther(e.target.value)}/>
                  </div>
                )}
                <div className="me-4">
                  <div className="field-label">Title Number</div>
                  <input
                    ref={titleNumberInputRef}
                    type="text"
                    name="title_number"
                    id="update-vehicle-title-number"
                    placeholder="123456"
                    value={titleNumber}
                    autoComplete="off"
                    onChange={(e) => setTitleNumber(e.target.value)}
                    onInput={(e) => setTitleNumber(e.currentTarget.value)}
                  />
                </div>
              </div>

              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <h6 className="text-primary mb-0">Check List</h6>
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  onClick={openCopyChecklistModal}
                >
                  Copy check list from other vehicle
                </Button>
              </div>
              <div className="app-main-content-fields-section column">
                {checklist.map((item, index) => (
                  <div className="mb-4" key={index}>
                    <input type="text" placeholder="e.g., Tire pressure" value={item} onChange={(e) => setChecklist([...checklist].map((a, index1) => {if (index1 === index) {return e.target.value;} return a;}))}/>
                    <button className="btn btn-link btn-sm" onClick={(e) => setChecklist([...checklist].filter((value, index1) => index1 != index))}>Remove</button>
                  </div>
                ))}
                <button className="btn btn-link" onClick={() => addItemToArray()}>+Add New Item</button>
              </div>

              <h6 className="text-primary">Additional Information</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Notes and Attachments</div>
                  <textarea placeholder="e.g., Vehicle assigned to Route A" value={note} onChange={e => setNote(e.target.value)} rows={4} style={{width: '400px'}}/>
                </div>
              </div>

              {error && <div className="col-md-12 mb-4 alert alert-danger" role="alert">
                {error}
              </div>}
              <div className="list row mb-5">
                <div className="col-md-12 col-sm-12 col-xs-12">
                  <button className="btn btn-default btn-sm float-right" onClick={() => navigateBackToView()}> Cancel </button>
                  <button className="btn btn-primary btn-sm float-right" onClick={() => saveVehicle()}> Save </button>
                </div>
              </div>
            </Tab>

            <Tab eventKey="complianceDeadlines" title="Compliance & Deadlines">
              <h6 className="text-primary">Compliance & Deadlines</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Insurance Expiration Date</div>
                  <DatePicker 
                    selected={insuranceStartDate} 
                    onChange={(date) => setInsuranceStartDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="e.g., 03/01/2024"
                    className="form-control"
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">Vehicle Registration Date</div>
                  <DatePicker 
                    selected={vehicleRegistrationDate} 
                    onChange={(date) => setVehicleRegistrationDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="e.g., 03/01/2024"
                    className="form-control"
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">Emissions</div>
                  <DatePicker
                    selected={emissionsDate}
                    onChange={(date) => setEmissionsDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="03/01/2024"
                    className="form-control"
                  />
                </div>
              </div>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Insurance Remaining Days</div>
                  <input
                    type="text"
                    readOnly
                    className="form-control"
                    style={{ background: '#f5f5f5' }}
                    placeholder="e.g., 50"
                    value={
                      insuranceStartDate
                        ? computeDaysFromTodayToDate(formatDateForBackend(insuranceStartDate))
                        : ''
                    }
                  />
                  <small className="text-muted d-block">Days from today to insurance expiration date</small>
                </div>
                <div className="me-4">
                  <div className="field-label">Registration Remaining Days</div>
                  <input
                    type="text"
                    readOnly
                    className="form-control"
                    style={{ background: '#f5f5f5' }}
                    placeholder="e.g., 50"
                    value={
                      vehicleRegistrationDate
                        ? computeDaysFromTodayToDate(formatDateForBackend(vehicleRegistrationDate))
                        : ''
                    }
                  />
                  <small className="text-muted d-block">Days from today to registration date</small>
                </div>
                <div className="me-4">
                  <div className="field-label">Emissions Remaining Days</div>
                  <input
                    type="text"
                    readOnly
                    className="form-control"
                    style={{ background: '#f5f5f5' }}
                    placeholder="e.g., 50"
                    value={
                      emissionsDate
                        ? computeDaysFromTodayToDate(formatDateForBackend(emissionsDate))
                        : ''
                    }
                  />
                  <small className="text-muted d-block">Days from today to emissions date</small>
                </div>
              </div>

              {error && <div className="col-md-12 mb-4 alert alert-danger" role="alert">
                {error}
              </div>}
              <div className="list row mb-5">
                <div className="col-md-12 col-sm-12 col-xs-12">
                  <button className="btn btn-default btn-sm float-right" onClick={() => navigateBackToView()}> Cancel </button>
                  <button className="btn btn-primary btn-sm float-right" onClick={() => saveVehicle()}> Save </button>
                </div>
              </div>
            </Tab>

          </Tabs>
          <div className="list-func-panel">
            {canArchiveVehicle && (
              <button className="btn btn-primary" onClick={() => triggerShowDeleteModal()}>
                {isArchivedVehicle ? (
                  <ArrowClockwise size={16} className="me-2" />
                ) : (
                  <Archive size={16} className="me-2" />
                )}
                {isArchivedVehicle ? 'Reactivate' : 'Archive'}
              </button>
            )}
          </div>
          </>
          )}
        </div>
      </div>
      <Modal show={showDeleteModal} onHide={() => closeDeleteModal()}>
        <Modal.Header closeButton>
          <Modal.Title>{isArchivedVehicle ? 'Reactivate Vehicle' : 'Archive Vehicle'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            {isArchivedVehicle
              ? 'Are you sure you want to reactivate this vehicle?'
              : 'Are you sure you want to archive this vehicle?'}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => closeDeleteModal()}>
            No
          </Button>
          <Button
            variant="primary"
            onClick={() => updateVehicleArchiveStatus(isArchivedVehicle ? 'active' : 'inactive')}
          >
            Yes
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCopyChecklistModal} onHide={closeCopyChecklistModal}>
        <Modal.Header closeButton>
          <Modal.Title>Copy check list from another vehicle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Select a vehicle below, then tap Copy. Your checklist fields will update; use Save to persist.
          </p>
          {otherVehiclesForChecklistCopy.length === 0 ? (
            <div className="text-muted">No other vehicles are available.</div>
          ) : (
            <div style={{ maxHeight: "55vh", overflowY: "auto" }}>
              {otherVehiclesForChecklistCopy.map((v) => (
                <div
                  role="button"
                  tabIndex={0}
                  key={v.id}
                  className={`card-container ${
                    String(copyChecklistSourceVehicleId) === String(v.id) ? "selected" : ""
                  }`}
                  onClick={() => setCopyChecklistSourceVehicleId(v.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setCopyChecklistSourceVehicleId(v.id);
                    }
                  }}
                >
                  <div className="fw-semibold">
                    {v.vehicle_number != null && v.vehicle_number !== ""
                      ? `Vehicle #${v.vehicle_number}`
                      : "Vehicle"}{" "}
                    {v.tag ? `(${v.tag})` : ""}
                  </div>
                  <div className="mt-1">
                    {Array.isArray(v.checklist) && v.checklist.length > 0 ? (
                      v.checklist.map((item, idx) => (
                        <small key={idx} className="me-2 d-inline-block">
                          {item}
                        </small>
                      ))
                    ) : (
                      <small className="text-muted">No checklist items</small>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeCopyChecklistModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={applyChecklistFromSelectedVehicle}
            disabled={!copyChecklistSourceVehicleId || otherVehiclesForChecklistCopy.length === 0}
          >
            Copy
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UpdateVehicle;
